import {
  faCaretDown, faCaretRight, faChevronDown, faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import React, {
  FC,
} from 'react';
import useSubjects from '../../hooks/useSubjects';
import Button from '../../lib/components/Button';
import Flex from '../../lib/components/Flex';
import FlexItem from '../../lib/components/FlexItem';
import { Grid, GridItem } from '../../lib/components/Grid';
import Hr from '../../lib/components/Hr';
import LoadingScreen from '../../lib/components/LoadingScreen';
import Pressable from '../../lib/components/Pressable';
import SizedBox from '../../lib/components/SizedBox';
import Toggle from '../../lib/components/Toggle';
import Typography from '../../lib/components/Typography';
import { useExamAnalysisContext } from '../../providers/ExamAnalysisProvider';
import Markdown from '../Markdown';
import ExamAnalysisForm from './ExamAnalysisForm';
import ExamAnalysisStyles from './ExamAnalysisStyles';
import VerticalLines from './VerticalLines';

const getDifficulty = (v: number): 'success' | 'warning' | 'danger' => {
  if (v < 3.4) {
    return 'success';
  }
  if (v < 6.7) {
    return 'warning';
  }
  return 'danger';
};

const transformBody = (body: string): string => {
  return body.replace(/!\[\]\(([^()]+)\)/g, '');
};

const getDifficultyBadge = (d: string): JSX.Element => {
  switch (d) {
    case 'EASY':
      return <div className="difficulty-success">Fácil</div>;
    case 'MEDIUM':
      return <div className="difficulty-warning">Média</div>;
    case 'HARD':
      return <div className="difficulty-danger">Difícil</div>;
    default:
      return <div className="difficulty-unknown">Desconhecida</div>;
  }
};

const ExamAnalysisContent: FC<{hideForm: boolean}> = ({ hideForm }) => {
  const {
    form,
    actions,
    depth,
    list,
    loading,
    showAnswer,
    open,
    questionOpen,
  } = useExamAnalysisContext();

  const { getSubjectInfoFromContents } = useSubjects();

  return (
    <>
      <Typography tag="h4" className="mb-4" normal>
        Explore os assuntos e questões que mais caíram
        {' '}
        {form.institution && `nas provas da ${form.institution}`}
        {' '}
        nos últimos 5 anos.
      </Typography>
      {!hideForm && (<ExamAnalysisForm />)}
      {
        form.institution && (
          <div className="card p-2 full-width">
            <Grid columnGap={2} rowGap={2}>
              <GridItem col={12} colLg={6}>
                <Flex align="center" className="mb-1">
                  <Flex align="center" className="mr-2">
                    <SizedBox backgroundColor="success" borderRadius={4} width={24} height={24} className="mr-1" />
                    <Typography tag="small" bold>Fácil</Typography>
                  </Flex>
                  <Flex align="center" className="mr-2">
                    <SizedBox backgroundColor="warning" borderRadius={4} width={24} height={24} className="mr-1" />
                    <Typography tag="small" bold>Médio</Typography>
                  </Flex>
                  <Flex align="center">
                    <SizedBox backgroundColor="danger" borderRadius={4} width={24} height={24} className="mr-1" />
                    <Typography tag="small" bold>Difícil</Typography>
                  </Flex>
                </Flex>
                <Typography className="mb-2" tag="p" appearance="small" bold>
                  Média da dificuldade das questões de cada&nbsp;
                  { depth === 2 ? 'especialidade' : 'tema' }
                </Typography>
              </GridItem>
              <GridItem col={12} colLg={6}>
                <Flex align="center">
                  <Typography className="mr-2">Visualizar por:</Typography>
                  <Button
                    size="small"
                    color="secondary"
                    variant={depth === 2 ? 'contained' : 'outlined'}
                    onClick={actions.onDepth(2)}
                    className="mr-2"
                  >
                    Especialidades
                  </Button>
                  <Button
                    size="small"
                    color="secondary"
                    variant={depth === 3 ? 'contained' : 'outlined'}
                    onClick={actions.onDepth(3)}
                  >
                    Temas
                  </Button>
                </Flex>
              </GridItem>
            </Grid>

            <Hr className="my-2" />

            <ExamAnalysisStyles />

            <Flex align="center" className="mb-2">
              <Toggle color="primary" value={showAnswer} onChange={actions.onShowAnswer} />
              <Typography className="ml-1">Mostrar a alternativa correta</Typography>
            </Flex>
            {
              list.length > 0 && (
                <Typography color="medium-gray" bold tag="small" className="mb-0">Nº de questões</Typography>
              )
            }
            <div className="pt-4 position-relative">
              <VerticalLines />
              {
                loading && <LoadingScreen />
              }
              {
                !loading && list.map((info) => {
                  const node = getSubjectInfoFromContents(info.subject);
                  const width = Math.min((info.questionCount * 100) / 20, 100) + (info.questionCount > 20 ? 1 : 0);

                  return (
                    <div key={info.subject} className="exam-bar__container">
                      <Typography ellipsis tag="small" bold tooltip={node?.subjectName} className="mb-0">
                        {node?.subjectName}
                      </Typography>
                      <Pressable className="exam-bar" onClick={actions.onOpen(info.subject)}>
                        <Flex align="center">
                          <FlexItem>
                            <Flex
                              width={`${width}%`}
                              height={30}
                              borderRadius={4}
                              backgroundColor={getDifficulty(info.difficulty)}
                              align="center"
                              justify="end"
                              className="px-2"
                            >
                              <FontAwesomeIcon
                                icon={open === info.subject ? faCaretRight : faCaretDown}
                                className="color-white"
                              />
                            </Flex>
                          </FlexItem>
                        </Flex>
                      </Pressable>
                      {
                        open === info.subject && (
                          <SizedBox
                            backgroundColor="light-gray"
                            className="full-width p-0"
                          >
                            {
                              info.questions.map((q, i) => {
                                return (
                                  <Pressable
                                    className="question-line"
                                    key={q.id}
                                    onClick={actions.onQuestionOpen(q.id)}
                                  >
                                    <FlexItem className="mr-1">
                                      {
                                        questionOpen === q.id ? (
                                          <>
                                            <Typography tag="small" className="mb-1">
                                              {
                                                getDifficultyBadge(q.difficulty)
                                              }
                                              <Markdown source={`**${i + 1})** ${q.body}`} />
                                            </Typography>
                                            {
                                              q.answers.map((answer, ai) => {
                                                const key = `${ai}-${q.id}`;
                                                return (
                                                  <Typography tag="p" tiny key={key} className="mb-0">
                                                    <b
                                                      className={clsx({
                                                        'color-success': ai === q.correctAnswer && showAnswer,
                                                      })}
                                                    >
                                                      {`${String.fromCharCode(ai + 65)})`}
                                                    </b>
                                                    &nbsp;
                                                    {answer}
                                                  </Typography>
                                                );
                                              })
                                            }
                                          </>
                                        ) : (
                                          <>
                                            {
                                              getDifficultyBadge(q.difficulty)
                                            }
                                            <Typography
                                              tag="small"
                                              appearance="p"
                                              lineClamp={3}
                                            >
                                              <b>{`${i + 1})`}</b>
                                              &nbsp;
                                              {transformBody(q.body)}
                                            </Typography>
                                          </>
                                        )
                                      }
                                    </FlexItem>
                                    <FontAwesomeIcon
                                      icon={questionOpen !== q.id ? faChevronDown : faChevronUp}
                                      className="color-dark-gray"
                                    />
                                  </Pressable>
                                );
                              })
                            }
                          </SizedBox>
                        )
                      }
                    </div>
                  );
                })
              }
            </div>
          </div>
        )
      }
    </>
  );
};

export default ExamAnalysisContent;
